"use client";

import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { ChevronDown, User } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

type AccountMenuTriggerButtonProps = ComponentPropsWithoutRef<"button"> & {
  variant?: "default" | "session";
  label?: string;
  expanded?: boolean;
  iconOnly?: boolean;
};

const accountMenuTriggerBaseClass =
  "theme-button-secondary inline-flex h-10 min-w-10 items-center gap-2 rounded-[0.95rem] border px-2.5 transition-[background,color,border-color,box-shadow,transform] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]";
const accountMenuTriggerDefaultClass =
  "text-foreground/80 hover:text-foreground";
const accountMenuTriggerSessionClass =
  "theme-control-surface text-foreground/85 hover:text-foreground";
const accountMenuTriggerLabelClass =
  "hidden max-w-[8.75rem] truncate text-[0.78rem] font-semibold tracking-[0.02em] sm:inline";
const accountMenuTriggerIconOnlyClass =
  "grid h-10 min-h-10 w-10 min-w-10 place-items-center gap-0 rounded-[0.95rem] px-0";

const AccountMenuTriggerButton = forwardRef<
  HTMLButtonElement,
  AccountMenuTriggerButtonProps
>(function AccountMenuTriggerButton(
  {
    className,
    variant = "default",
    children,
    type = "button",
    label = "Account",
    expanded = false,
    iconOnly = false,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        accountMenuTriggerBaseClass,
        variant === "session"
          ? accountMenuTriggerSessionClass
          : accountMenuTriggerDefaultClass,
        iconOnly && accountMenuTriggerIconOnlyClass,
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          <User className={cn("shrink-0", iconOnly ? "size-[1.05rem]" : "size-[1rem]")} />
          {!iconOnly ? (
            <>
              <span className={accountMenuTriggerLabelClass}>{label}</span>
              <ChevronDown
                className={cn(
                  "size-3.5 shrink-0 transition-transform duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
                  expanded ? "rotate-180" : null,
                )}
              />
            </>
          ) : null}
        </>
      )}
    </button>
  );
});

export default AccountMenuTriggerButton;
